import { SearchIcon } from 'lucide-react';

import { Button } from '@/shared/ui/shadcn/button';
import { Input } from '@/shared/ui/shadcn/input';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import type { MedicationSearchRow } from '../../model/useMedicationSearchPicker';

interface MedicationSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchText: string;
  onSearchTextChange: (text: string) => void;
  onCloseResetSearchText: () => void;
  isSearchTextValid: boolean;
  searchTextError: string | null;
  isSelecting: boolean;
  isSearchPending: boolean;
  isSearchError: boolean;
  onRetrySearch: () => void;
  results: MedicationSearchRow[];
  onSelectMedication: (row: MedicationSearchRow) => void;
  selectError: string | null;
  triggerLabel: string;
  triggerDisabled: boolean;
  triggerAriaInvalid?: boolean;
}

export function MedicationSearchDialog({
  open,
  onOpenChange,
  searchText,
  onSearchTextChange,
  onCloseResetSearchText,
  isSearchTextValid,
  searchTextError,
  isSelecting,
  isSearchPending,
  isSearchError,
  onRetrySearch,
  results,
  onSelectMedication,
  selectError,
  triggerLabel,
  triggerDisabled,
  triggerAriaInvalid,
}: MedicationSearchDialogProps) {
  return (
    <>
      <div className="relative">
        <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          readOnly
          value={isSelecting ? 'Loading medication…' : triggerLabel}
          onClick={() => onOpenChange(true)}
          onFocus={() => onOpenChange(true)}
          aria-invalid={triggerAriaInvalid}
          aria-busy={isSelecting}
          disabled={triggerDisabled}
          className="cursor-pointer pl-9"
        />
      </div>

      <CommandDialog
        open={open}
        onOpenChange={(nextOpen) => {
          onOpenChange(nextOpen);
          if (!nextOpen) {
            onCloseResetSearchText();
          }
        }}
        title="Search medications"
        description="Type to search medications."
      >
        <CommandInput
          value={searchText}
          onValueChange={onSearchTextChange}
          placeholder="Search medications..."
          autoFocus
        />
        <CommandList>
          {searchText.trim().length === 0 ? (
            <CommandEmpty>Type a medication name to search.</CommandEmpty>
          ) : !isSearchTextValid ? (
            <CommandEmpty>
              {searchTextError ?? 'Type at least 3 characters to search medications.'}
            </CommandEmpty>
          ) : isSelecting ? (
            <CommandEmpty>Loading medication details…</CommandEmpty>
          ) : isSearchPending ? (
            <CommandEmpty>Searching…</CommandEmpty>
          ) : isSearchError ? (
            <CommandEmpty>
              Search failed.
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void onRetrySearch()}
                >
                  Retry
                </Button>
              </div>
            </CommandEmpty>
          ) : results.length === 0 ? (
            <CommandEmpty>No medications found.</CommandEmpty>
          ) : (
            <CommandGroup heading="Results">
              {results.map((row) => (
                <CommandItem
                  key={String(row.dispensableDrugId)}
                  // Include NDC + DispensableDrugId in the searchable
                  // value so the cmdk fuzzy-matcher hits when the user
                  // pastes an id directly.
                  value={[
                    row.label,
                    row.strength ?? '',
                    row.ndc ?? '',
                    String(row.dispensableDrugId),
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={row.isObsolete}
                  onSelect={() => onSelectMedication(row)}
                >
                  <div className="flex min-w-0 flex-col">
                    <div className="truncate">
                      {row.label}
                      {row.isObsolete ? ' (obsolete)' : ''}
                    </div>
                    {row.strength ? (
                      <div className="text-muted-foreground truncate text-xs">{row.strength}</div>
                    ) : null}
                    <div className="text-muted-foreground flex flex-wrap gap-x-3 font-mono text-[10px] leading-tight">
                      {row.ndc ? <span>NDC {row.ndc}</span> : null}
                      <span>DDID {row.dispensableDrugId}</span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
        {selectError ? (
          <div className="text-destructive border-t px-3 py-2 text-sm">{selectError}</div>
        ) : null}
      </CommandDialog>
    </>
  );
}
